import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import { packChatPreview } from "@/payroll/pack-chat/message-preview";
import { PackChatStatusMark } from "@/payroll/pack-chat/pack-chat-status-mark";
import type { IPackChatTechnicianGroup } from "@/payroll/pack-chat/types";

const technicianSummary = (group: IPackChatTechnicianGroup) => {
  const total = group.threads.length;
  const plural = total === 1 ? "" : "s";
  const count =
    group.openCount === 0
      ? `${total} thread${plural}`
      : group.openCount === total
        ? `${total} open`
        : `${total} threads · ${group.openCount} open`;

  return `${count} · ${dayjs(group.lastUpdatedAt).fromNow()}`;
};

export const PackChatTechnicianRow = ({
  group,
  selected,
  onSelect,
}: {
  group: IPackChatTechnicianGroup;
  selected: boolean;
  onSelect: () => void;
}) => {
  const open = group.openCount > 0;
  const last = group.lastMessageThread?.last_message;
  const preview = last ? packChatPreview(last) : null;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "w-full px-3 py-2.5 text-left transition-colors hover:bg-surface-secondary",
        { "bg-surface-secondary": selected },
      )}
    >
      <Stack direction="row" align="start" justify="between" gap="sm" fullWidth>
        <Stack gap="none" align="stretch" className="min-w-0">
          <Typography variant="subtitleW3" className="truncate">
            {group.technician.name}
          </Typography>
          <Typography
            variant="bodyW3"
            textColor="tertiary"
            className="truncate text-[12px]"
          >
            {technicianSummary(group)}
          </Typography>
          {last && (
            <Typography
              variant="bodyW3"
              textColor="tertiary"
              className="truncate pt-0.5 text-[13px]"
            >
              {preview &&
                (preview.author
                  ? `${preview.author}: ${preview.body}`
                  : preview.body)}
            </Typography>
          )}
        </Stack>
        <PackChatStatusMark
          focusable={false}
          hasAiMessages={group.hasAiMessages}
          needsManager={group.needsManager}
          responseOwner={group.responseOwner}
          open={open}
          label={open ? `${group.openCount} open` : "All closed"}
          className="mt-0.5"
        />
      </Stack>
    </button>
  );
};
